import * as React from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router";

import {
  useCreateSupplierSummaryCollectionMutation,
  useGetLatestCollectionSummariesRunQuery,
} from "@/api/contract-family-pipeline";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCanConfigureContractFamilies } from "@/hooks/use-contract-families-access";
import { type SummaryColumn, SummaryColumnsField } from "@/modules/snapshots/grid/components/SummaryColumnsField";
import { useCanSummariseSupplierCollection } from "@/modules/snapshots/grid/useCanSummariseSupplierCollection";
import { buildSnapshotGridPath } from "@/modules/snapshots/routes";
import { snapshotCollectionApi } from "@/modules/snapshots/snapshotCollection.api";
import { SNAPSHOT_COLLECTION_TAGS } from "@/modules/snapshots/snapshotCollection.constraints";
import type { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import type { AutomatedRunStatus, PipelineRunResponse } from "@/types/contract-family-pipeline";
import { CancelAutomatedRunButton } from "@/views/contract-families/components/CancelAutomatedRunButton";
import { ContractSummarySettingsNotice } from "@/views/contract-families/components/ContractSummarySettingsNotice";
import { UseBatchApiField } from "@/views/contract-families/components/UseBatchApiField";
import { useBatchApiChoice } from "@/views/contract-families/components/useBatchApiChoice";
import { useContractSummaryReadiness } from "@/views/contract-families/components/useContractSummaryReadiness";
import { buildContractFamiliesPath } from "@/views/contract-families/navigation";
import {
  getContractSummaryFailureMessage,
  getPipelineErrorMessage,
} from "@/views/contract-families/pipeline/pipelineErrors";

export const SUPPLIER_SUMMARY_COLLECTION_LABEL = "Create Supplier Summary Collection";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapshotCollectionId: string;
  /** The template columns the user can choose to regenerate; all are chosen when the dialog opens. */
  columns?: SummaryColumn[];
  /** The collection's notice refreshes on its own; this is for callers that want the started run. */
  onStarted?: (run: PipelineRunResponse) => void;
}

/** Confirms summarising every contract family of a base collection into the Supplier Summaries Collection. */
export const SupplierSummaryCollectionDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  snapshotCollectionId,
  columns = [],
  onStarted,
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const [createSummaries, { isLoading }] = useCreateSupplierSummaryCollectionMutation();
  const { useBatchApi, setUseBatchApi, executionMode } = useBatchApiChoice(open);
  const { isChecking, missing, mismatch } = useContractSummaryReadiness(open);
  const isConfigured = missing.length === 0;
  const orderedColumns = React.useMemo(() => [...columns].sort((a, b) => a.order - b.order), [columns]);
  // A refetched template hands over a new array; only a different set of columns resets the choice.
  const columnKey = orderedColumns.map(column => column.id).join("\n");
  const [selectedIds, setSelectedIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const noColumnSelected = orderedColumns.length > 0 && selectedIds.size === 0;

  React.useEffect(() => {
    if (open) setError(null);
  }, [open]);

  React.useEffect(() => {
    if (open) setSelectedIds(new Set(columnKey ? columnKey.split("\n") : []));
  }, [open, columnKey]);

  const submit = async () => {
    setError(null);
    // Every column chosen is the full run; only a strict subset leaves cells as published.
    const fieldIds = orderedColumns.filter(column => selectedIds.has(column.id)).map(column => column.id);
    try {
      const run = await createSummaries({
        snapshot_collection_id: snapshotCollectionId,
        ...(executionMode ? { execution_mode: executionMode } : {}),
        ...(fieldIds.length < orderedColumns.length ? { field_ids: fieldIds } : {}),
      }).unwrap();
      onStarted?.(run);
      onOpenChange(false);
    } catch (requestError) {
      setError(getPipelineErrorMessage(requestError));
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => (next || !isLoading ? onOpenChange(next) : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{SUPPLIER_SUMMARY_COLLECTION_LABEL}</DialogTitle>
          <DialogDescription>
            This summarises every contract family in this collection and publishes the results to the Supplier Summaries
            Collection, listed under this template in Collections. Existing family rows are replaced by a new version.
          </DialogDescription>
        </DialogHeader>
        {isConfigured ? (
          <>
            <UseBatchApiField
              id="supplier-summary-collection-use-batch-api"
              checked={useBatchApi}
              onCheckedChange={setUseBatchApi}
              disabled={isLoading}
            />
            {orderedColumns.length > 0 ? (
              <SummaryColumnsField
                columns={orderedColumns}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
                disabled={isLoading}
              />
            ) : null}
          </>
        ) : (
          <ContractSummarySettingsNotice missing={missing} mismatch={mismatch} />
        )}
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void submit()}
            disabled={isLoading || isChecking || !isConfigured || noColumnSelected}
          >
            {isLoading ? "Creating…" : SUPPLIER_SUMMARY_COLLECTION_LABEL}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface NoticeProps {
  snapshotCollection: Pick<SnapshotCollection, "id" | "business_report_id" | "is_family_collection">;
}

const NOTICE_POLL_INTERVAL_MS = 15_000;
const isLiveRun = (run: AutomatedRunStatus) => run.stage !== "complete" && run.stage !== "failed";

/** Shows on a base collection where its latest supplier summaries run stands; it survives a reload. */
export const SupplierSummaryCollectionNotice: React.FC<NoticeProps> = ({ snapshotCollection }) => {
  const canSummarise = useCanSummariseSupplierCollection(snapshotCollection);
  const canConfigure = useCanConfigureContractFamilies();
  const [isLive, setIsLive] = React.useState(false);
  const latest = useGetLatestCollectionSummariesRunQuery(snapshotCollection.id, {
    skip: !canSummarise,
    pollingInterval: isLive ? NOTICE_POLL_INTERVAL_MS : 0,
    refetchOnMountOrArgChange: true,
  });
  const run = canSummarise && !latest.isError ? (latest.data ?? null) : null;
  const live = run !== null && isLiveRun(run);
  const dispatch = useDispatch();
  const wasLiveRef = React.useRef(false);

  React.useEffect(() => {
    setIsLive(live);
  }, [live]);

  React.useEffect(() => {
    // The requester gains access to the Supplier Summaries Collection with the run: once it ends,
    // the cached collections list must be read again or the link below leads to a collection the
    // sidebar and the grid page do not know yet.
    if (wasLiveRef.current && !live) {
      dispatch(snapshotCollectionApi.util.invalidateTags([SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG]));
    }
    wasLiveRef.current = live;
  }, [dispatch, live]);

  if (!run) return null;

  if (live) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-700"
      >
        <span>Supplier Summaries Collection is being generated.</span>
        {run.is_mine ? (
          <Link
            to={buildContractFamiliesPath(run.source_folder_fse_id, { pipelineRunId: run.id })}
            className="font-medium underline"
          >
            View progress
          </Link>
        ) : null}
        {run.is_mine || canConfigure ? <CancelAutomatedRunButton runId={run.id} /> : null}
      </div>
    );
  }

  if (run.stage === "failed" && run.error_code !== "cancelled") {
    return (
      <div
        role="alert"
        className="mb-4 rounded-md border border-[#efc2bd] bg-[#fde8e6] px-3 py-2 text-sm text-[#9e3025]"
      >
        <span className="font-medium">Supplier Summaries Collection could not be generated:</span>{" "}
        {getContractSummaryFailureMessage(run.error_code)}
      </div>
    );
  }

  if (run.stage === "complete" && run.target_family_collection_id && run.fixed_template_id) {
    return (
      <div
        role="status"
        className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-700"
      >
        <span>Supplier Summaries Collection is ready.</span>
        <Link
          to={buildSnapshotGridPath(run.fixed_template_id, run.target_family_collection_id)}
          className="font-medium underline"
        >
          Open Supplier Summaries Collection
        </Link>
      </div>
    );
  }

  return null;
};
